import { forwardRef } from "react";
import { FieldError, useWatch } from "react-hook-form";
import {
  AlignItems,
  Box,
  Field,
  FormFieldStatus,
  Hint,
  Label,
  Option,
  Select,
} from "@gocardless/flux-react";
import { t, Trans } from "@lingui/macro";
import { useLingui } from "@lingui/react";
import { CreditorType } from "@gocardless/api/dashboard/types";

import { creditorTypeList } from "../consts";

import { getInputErrorStatus } from "src/utils/inputValidation";
import { FieldLevelError } from "src/common/api";
import { useSupportedCountries } from "src/libraries/creditor/hooks";
import { CountryCodeSupported } from "src/common/country";
import { enumFromStringValue } from "src/utils/types";

interface CreditorTypeDropdownProps {
  geoValue: string;
  error?: FieldError;
  apiError?: FieldLevelError;
}

export const CreditorTypeDropdown = forwardRef<
  HTMLSelectElement,
  CreditorTypeDropdownProps
>(({ geoValue, error, apiError, ...field }, ref) => {
  const { isCreditorTypeSupported } = useSupportedCountries();

  const { i18n } = useLingui();
  const creditorValue = useWatch({ name: `creditor_type` });
  const generateCreditorTypeOptions = (creditorType: typeof CreditorType) =>
    Object.values(creditorType).map((type) => (
      <Option key={type} value={type} data-testid={type}>
        {creditorTypeList(i18n)[type as CreditorType]}
      </Option>
    ));

  const creditorTypeOptions = generateCreditorTypeOptions(CreditorType);

  const invalidCreditorType = !isCreditorTypeSupported(
    enumFromStringValue(CountryCodeSupported, geoValue),
    enumFromStringValue(CreditorType, creditorValue)
  );

  const hasCreditorTypeError = geoValue && creditorValue && invalidCreditorType;

  return (
    <Field data-testid="creditor_type">
      <Box layout="flex" alignItems={AlignItems.Center}>
        <Label htmlFor="creditor_type">
          <Trans id="sign-up.form.creditor_type.label">Business type</Trans>
        </Label>
      </Box>
      <Select
        id="creditor_type"
        data-testid="select_creditor_type"
        name="creditor_type"
        aria-label="creditor_type"
        ref={ref}
        defaultValue="Choose your business type"
        status={getInputErrorStatus(
          !!error || (!!creditorValue && invalidCreditorType)
        )}
        required
        {...field}
      >
        {[
          <Option key="default" value="">
            {t({
              id: "sign-up.form.creditor_type.default-value",
              message: "Choose your business type",
            })}
          </Option>,
          ...creditorTypeOptions,
        ]}
      </Select>
      {error && (
        <Hint status={FormFieldStatus.Danger}>
          <Trans id="sign-up.form.creditor_type.field-required-warning">
            Business type is required
          </Trans>
        </Hint>
      )}
      {apiError && (
        <Hint status={FormFieldStatus.Danger}>{apiError.message}</Hint>
      )}
      {hasCreditorTypeError && (
        <Hint status={FormFieldStatus.Danger}>
          <Trans id="sign-up.form.creditor-and-geo.warning">
            Unfortunately we are not able to serve this business type in this
            country.
          </Trans>
        </Hint>
      )}
    </Field>
  );
});
